/**
 * Terminal Display Switch Button Component
 * Shows/hides based on admin_display_switch permission
 * Green when ON, Red when OFF
 */

import React, { useState, useEffect } from 'react';
import { terminalDisplayService } from '../services/terminalDisplayService';
import { usePermissions } from '../lib/permissions';
import toast from 'react-hot-toast';
import { EyeIcon, EyeSlashIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface TerminalDisplaySwitchProps {
  terminalId: number;
  onToggle?: (newState: boolean) => void;
  className?: string;
}

export const TerminalDisplaySwitch: React.FC<TerminalDisplaySwitchProps> = ({
  terminalId,
  onToggle,
  className = ''
}) => {
  const [displayFlag, setDisplayFlag] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const permissions = usePermissions();

  // Check if user has permission to see this button
  const hasDisplaySwitchPermission = permissions.hasPermission('admin_display_switch');

  // Fetch initial display flag status
  useEffect(() => {
    if (!hasDisplaySwitchPermission) return;

    const fetchDisplayFlag = async () => {
      setFetching(true);
      try {
        const flag = await terminalDisplayService.getDisplayFlag(terminalId);
        setDisplayFlag(flag);
      } catch (error) {
        console.error('Failed to fetch display flag:', error);
        toast.error('Failed to load display status');
      } finally {
        setFetching(false);
      }
    };

    fetchDisplayFlag();
  }, [terminalId, hasDisplaySwitchPermission]);

  // Handle toggle
  const handleToggle = async () => {
    if (loading || displayFlag === null) return;

    setLoading(true);
    try {
      const newFlag = await terminalDisplayService.toggleDisplayFlag(
        terminalId,
        'Dashboard User', // You can pass actual user email if available
        'Display switch toggled from dashboard'
      );
      
      setDisplayFlag(newFlag);
      toast.success(`Display ${newFlag ? 'enabled' : 'disabled'} for Terminal ${terminalId}`);
      
      // Call parent callback if provided
      if (onToggle) {
        onToggle(newFlag);
      }
    } catch (error) {
      console.error('Failed to toggle display flag:', error);
      toast.error('Failed to toggle display status');
    } finally {
      setLoading(false);
    }
  };

  // Don't render if no permission
  if (!hasDisplaySwitchPermission) {
    return null;
  }

  // Show loading state
  if (fetching) {
    return (
      <button
        disabled
        className={`px-4 py-2 rounded-md text-sm font-medium bg-gray-700 text-gray-400 cursor-not-allowed ${className}`}
      >
        Loading...
      </button>
    );
  }

  return (
    <button
      onClick={handleToggle}
      disabled={loading || displayFlag === null}
      className={`
        px-4 py-2 rounded-md text-sm font-medium
        transition-all duration-200 ease-in-out
        flex items-center space-x-2
        ${displayFlag 
          ? 'bg-green-600 hover:bg-green-700 text-white' 
          : 'bg-red-600 hover:bg-red-700 text-white'
        }
        ${loading || displayFlag === null ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${className}
      `}
      title={displayFlag ? 'Display is ON - Click to turn OFF' : 'Display is OFF - Click to turn ON'}
    >
      {displayFlag ? (
        <>
          <EyeIcon className="h-4 w-4" />
          <span>Display Shutdown</span>
        </>
      ) : (
        <>
          <EyeSlashIcon className="h-4 w-4" />
          <span>Display Shutdown</span>
        </>
      )}
      {loading && (
        <div className="ml-2 animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
      )}
    </button>
  );
};

export default TerminalDisplaySwitch;